// Service de synchronisation automatique avec Supabase
import { supabase } from '../utils/supabase/client';

class SupabaseSync {
  constructor() {
    this.isOnline = navigator.onLine;
    this.setupNetworkListeners();
  }

  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncAllPendingData();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // ===================== PROJETS =====================
  
  async syncProject(projectData, operation = 'upsert') {
    try {
      if (!this.isOnline) {
        this.queueOperation('projects', projectData, operation);
        return { success: false, queued: true };
      }

      switch (operation) {
        case 'insert':
          const { data: newProject, error: insertError } = await supabase
            .from('projects')
            .insert(projectData)
            .select()
            .single();
          
          if (insertError) throw insertError;
          
          // Mettre à jour localStorage
          this.updateLocalStorage('projects', newProject, 'add');
          return { success: true, data: newProject };

        case 'update':
          const { data: updatedProject, error: updateError } = await supabase
            .from('projects')
            .update(projectData)
            .eq('id', projectData.id)
            .select()
            .single();
          
          if (updateError) throw updateError;
          
          this.updateLocalStorage('projects', updatedProject, 'update');
          return { success: true, data: updatedProject };

        case 'upsert':
        default:
          const { data: upsertedProject, error: upsertError } = await supabase
            .from('projects')
            .upsert(projectData)
            .select()
            .single();
          
          if (upsertError) throw upsertError;
          
          this.updateLocalStorage('projects', upsertedProject, 'upsert');
          return { success: true, data: upsertedProject };
      }
    } catch (error) {
      console.error('Erreur sync projet:', error);
      // En cas d'erreur, sauvegarder en local
      this.updateLocalStorage('projects', projectData, operation);
      this.queueOperation('projects', projectData, operation);
      return { success: false, error: error.message };
    }
  }

  async getAllProjects(userId = null) {
    try {
      let query = supabase.from('projects').select('*');
      
      if (userId) {
        query = query.eq('creator_id', userId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Synchroniser avec localStorage
      const existingProjects = JSON.parse(localStorage.getItem('projects') || '[]');
      const mergedProjects = this.mergeProjectData(existingProjects, data);
      localStorage.setItem('projects', JSON.stringify(mergedProjects));
      
      return { success: true, data: mergedProjects };
    } catch (error) {
      console.error('Erreur récupération projets:', error);
      // Fallback sur localStorage
      const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
      return { success: false, data: localProjects, error: error.message };
    }
  }

  // ===================== INVESTISSEMENTS =====================
  
  async syncInvestment(investmentData) {
    try {
      if (!this.isOnline) {
        this.queueOperation('investments', investmentData, 'insert');
        return { success: false, queued: true };
      }

      const { data, error } = await supabase
        .from('investments')
        .insert({
          investor_id: investmentData.userId,
          project_id: investmentData.projectId,
          amount: parseFloat(investmentData.amount),
          payment_method: investmentData.paymentMethod,
          expected_return: parseFloat(investmentData.expectedReturn || 0),
          investment_date: investmentData.date || new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Mettre à jour localStorage
      this.updateLocalStorage('investments', data, 'add');
      
      // Mettre à jour le projet (montant reçu)
      await this.updateProjectFunding(investmentData.projectId, parseFloat(investmentData.amount), investmentData.userId);
      
      return { success: true, data };
    } catch (error) {
      console.error('Erreur sync investissement:', error);
      this.updateLocalStorage('investments', investmentData, 'add');
      this.queueOperation('investments', investmentData, 'insert');
      return { success: false, error: error.message };
    }
  }

  async getUserInvestments(userId) {
    try {
      const { data, error } = await supabase
        .from('investments')
        .select(`
          *,
          projects (
            titre,
            description,
            image_url
          )
        `)
        .eq('investor_id', userId)
        .order('investment_date', { ascending: false });

      if (error) throw error;

      // Synchroniser avec localStorage
      const localKey = `investments_${userId}`;
      localStorage.setItem(localKey, JSON.stringify(data));
      
      return { success: true, data };
    } catch (error) {
      console.error('Erreur récupération investissements:', error);
      const localInvestments = JSON.parse(localStorage.getItem(`investments_${userId}`) || '[]');
      return { success: false, data: localInvestments, error: error.message };
    }
  }

  // ===================== PROFILS UTILISATEUR =====================
  
  async syncUserProfile(userData) {
    try {
      if (!this.isOnline) {
        this.queueOperation('user_profiles', userData, 'upsert');
        return { success: false, queued: true };
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          id: userData.id,
          email: userData.email,
          nom: userData.nom,
          prenom: userData.prenom,
          telephone: userData.telephone,
          ville: userData.ville,
          date_naissance: userData.dateNaissance,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Mettre à jour localStorage
      localStorage.setItem('currentUser', JSON.stringify(data));
      
      return { success: true, data };
    } catch (error) {
      console.error('Erreur sync profil:', error);
      this.queueOperation('user_profiles', userData, 'upsert');
      return { success: false, error: error.message };
    }
  }

  // ===================== FONCTIONS UTILITAIRES =====================
  
  updateLocalStorage(type, data, operation) {
    const key = type;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    
    switch (operation) {
      case 'add':
        existing.push(data);
        break;
      case 'update':
        const updateIndex = existing.findIndex(item => item.id === data.id);
        if (updateIndex !== -1) {
          existing[updateIndex] = { ...existing[updateIndex], ...data };
        }
        break;
      case 'upsert':
        const upsertIndex = existing.findIndex(item => item.id === data.id);
        if (upsertIndex !== -1) {
          existing[upsertIndex] = { ...existing[upsertIndex], ...data };
        } else {
          existing.push(data);
        }
        break;
      default:
        console.warn('Opération inconnue:', operation);
        break;
    }
    
    localStorage.setItem(key, JSON.stringify(existing));
  }

  queueOperation(table, data, operation) {
    const queue = JSON.parse(localStorage.getItem('syncQueue') || '[]');
    queue.push({
      table,
      data,
      operation,
      timestamp: Date.now()
    });
    localStorage.setItem('syncQueue', JSON.stringify(queue));
  }

  async syncAllPendingData() {
    const queue = JSON.parse(localStorage.getItem('syncQueue') || '[]');
    
    for (const item of queue) {
      try {
        switch (item.table) {
          case 'projects':
            await this.syncProject(item.data, item.operation);
            break;
          case 'investments':
            await this.syncInvestment(item.data);
            break;
          case 'user_profiles':
            await this.syncUserProfile(item.data);
            break;
          default:
            console.warn('Table inconnue:', item.table);
            break;
        }
      } catch (error) {
        console.error('Erreur sync queue:', error);
      }
    }
    
    // Vider la queue après sync
    localStorage.setItem('syncQueue', JSON.stringify([]));
  }

  async updateProjectFunding(projectId, amount, investorId) {
    try {
      // Un investisseur qui réinvestit dans le même projet ne compte pas comme un nouveau membre :
      // l'investissement vient d'être enregistré, donc « premier » = une seule ligne pour ce couple.
      let isNewMember = true;
      if (investorId) {
        const { count, error: countError } = await supabase
          .from('investments')
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .eq('investor_id', investorId);
        if (!countError) isNewMember = (count || 0) <= 1;
      }

      // Récupérer le projet actuel
      const { data: project, error: fetchError } = await supabase
        .from('projects')
        .select('montant_recu, membres')
        .eq('id', projectId)
        .single();

      if (fetchError) throw fetchError;

      // Mettre à jour les montants
      const newMontantRecu = (project.montant_recu || 0) + amount;
      const newMembres = (project.membres || 0) + (isNewMember ? 1 : 0);

      const { error: updateError } = await supabase
        .from('projects')
        .update({
          montant_recu: newMontantRecu,
          membres: newMembres
        })
        .eq('id', projectId);

      if (updateError) throw updateError;

      // Mettre à jour localStorage
      const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
      const projectIndex = localProjects.findIndex(p => p.id === projectId);
      if (projectIndex !== -1) {
        localProjects[projectIndex].montantRecu = newMontantRecu;
        localProjects[projectIndex].membres = newMembres;
        localStorage.setItem('projects', JSON.stringify(localProjects));
      }

    } catch (error) {
      console.error('Erreur mise à jour financement:', error);
    }
  }

  mergeProjectData(localProjects, remoteProjects) {
    const merged = [...localProjects];
    
    remoteProjects.forEach(remoteProject => {
      const existingIndex = merged.findIndex(p => p.id === remoteProject.id);
      if (existingIndex !== -1) {
        // Fusionner en privilégiant les données distantes plus récentes
        merged[existingIndex] = {
          ...merged[existingIndex],
          ...remoteProject,
          // Conserver certaines données locales si plus récentes
          lastModified: Math.max(
            new Date(merged[existingIndex].lastModified || 0).getTime(),
            new Date(remoteProject.updated_at || 0).getTime()
          )
        };
      } else {
        merged.push(remoteProject);
      }
    });
    
    return merged;
  }

  // ===================== SYNCHRONISATION TEMPS RÉEL =====================
  
  setupRealtimeSync(userId) {
    // Écouter les changements sur les projets
    const projectsChannel = supabase
      .channel('projects-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'projects'
      }, (payload) => {
        this.handleRealtimeUpdate('projects', payload);
      })
      .subscribe();

    // Écouter les changements sur les investissements de l'utilisateur
    const investmentsChannel = supabase
      .channel('investments-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'investments',
        filter: `investor_id=eq.${userId}`
      }, (payload) => {
        this.handleRealtimeUpdate('investments', payload);
      })
      .subscribe();

    return { projectsChannel, investmentsChannel };
  }

  handleRealtimeUpdate(table, payload) {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    switch (eventType) {
      case 'INSERT':
        this.updateLocalStorage(table, newRecord, 'add');
        break;
      case 'UPDATE':
        this.updateLocalStorage(table, newRecord, 'update');
        break;
      case 'DELETE':
        this.removeFromLocalStorage(table, oldRecord.id);
        break;
      default:
        console.warn('Événement inconnu:', eventType);
        break;
    }
    
    // Déclencher un événement global pour mettre à jour l'UI
    window.dispatchEvent(new CustomEvent('supabase-sync-update', {
      detail: { table, eventType, data: newRecord || oldRecord }
    }));
  }

  removeFromLocalStorage(type, id) {
    const existing = JSON.parse(localStorage.getItem(type) || '[]');
    const filtered = existing.filter(item => item.id !== id);
    localStorage.setItem(type, JSON.stringify(filtered));
  }
}

const supabaseSyncInstance = new SupabaseSync();
export default supabaseSyncInstance;